import { useCallback, useEffect, useRef, useState } from "react";

import { startGetBrandInformation } from "../../../../actions/adsCampaigns";
import {
  startGetCompetitorAdsCache,
  startInvalidateCompetitorAdsCache,
  startListenerCompetitorAdsCache,
  stopListenerCompetitorAdsCache,
} from "../../../../actions/competitorAdsFavorites";
import {
  startGetCompetitorsAds,
  startPersistMetaAdLibraryImageToStorage,
} from "../../../../actions/user";
import { normalizeApiDataToForm } from "../../../../pages/Settings/BrandSetup/utils/formDataTransformers";
import {
  formatCompetitorAdsCacheForGallery,
  isMetaAdLibraryViewOnlyItem,
  normalizeCompetitorName,
} from "../../../../services/competitorAdsGallery";
import { ALL_FILTER } from "../../../../utils/constants";
import {
  COMPETITORS_CACHED_LOADING_MIN_MS,
  COMPETITORS_LOADING_TIME_MS,
} from "../constants";

const waitForCachedLoadingMinimum = async ({ loadingStartedAt }) => {
  const remaining =
    COMPETITORS_CACHED_LOADING_MIN_MS - (Date.now() - loadingStartedAt);

  if (remaining <= 0) return;

  await new Promise((resolve) => {
    setTimeout(resolve, remaining);
  });
};

const getCompetitorNamesInData = (items) =>
  new Set(
    (items || [])
      .map((item) => normalizeCompetitorName(item?.competitor))
      .filter(Boolean)
  );

const getUncachedCompetitorNames = ({ competitorNames, items }) => {
  const namesInData = getCompetitorNamesInData(items);

  return (competitorNames || [])
    .map((name) => name.trim())
    .filter((name) => name && !namesInData.has(normalizeCompetitorName(name)));
};

export const useCompetitorsData = ({
  dispatch,
  pathStorageReferenceAd,
  modalOpen,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingContentReady, setIsLoadingContentReady] = useState(false);
  const [loadingDurationMs, setLoadingDurationMs] = useState(
    COMPETITORS_LOADING_TIME_MS
  );
  const [isUploading, setIsUploading] = useState(false);
  const [isScrapingNewCompetitors, setIsScrapingNewCompetitors] =
    useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [businessCompetitors, setBusinessCompetitors] = useState([]);
  const fetchInFlightRef = useRef(false);
  const pendingRefreshRef = useRef(null);
  const pendingScrapeNamesRef = useRef([]);
  const dataRef = useRef(data);
  const cacheListenerRef = useRef(null);
  const cacheApplyTimerRef = useRef(null);
  const cacheInvalidationInProgressRef = useRef(false);
  const modalOpenRef = useRef(modalOpen);

  useEffect(() => {
    modalOpenRef.current = modalOpen;
  }, [modalOpen]);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const clearScrapingState = useCallback(() => {
    pendingScrapeNamesRef.current = [];
    setIsScrapingNewCompetitors(false);
  }, []);

  const clearPendingCacheSnapshot = useCallback(() => {
    if (cacheApplyTimerRef.current) {
      clearTimeout(cacheApplyTimerRef.current);
      cacheApplyTimerRef.current = null;
    }
  }, []);

  const resetGalleryState = useCallback(() => {
    clearPendingCacheSnapshot();
    clearScrapingState();
    setData([]);
    setError(null);
    dataRef.current = [];
  }, [clearPendingCacheSnapshot, clearScrapingState]);

  const startScrapingForCompetitors = useCallback(({ competitorNames }) => {
    const uncachedNames = getUncachedCompetitorNames({
      competitorNames,
      items: dataRef.current,
    });

    if (uncachedNames.length === 0) return;

    pendingScrapeNamesRef.current = uncachedNames;
    setIsScrapingNewCompetitors(true);
  }, []);

  const syncScrapingStateWithData = useCallback(
    (items) => {
      if (pendingScrapeNamesRef.current.length === 0) return;

      const stillPending = getUncachedCompetitorNames({
        competitorNames: pendingScrapeNamesRef.current,
        items,
      });

      if (stillPending.length === 0) {
        clearScrapingState();
        return;
      }

      pendingScrapeNamesRef.current = stillPending;
    },
    [clearScrapingState]
  );

  const applyCompetitorsResponse = useCallback(
    (competitorsResponse) => {
      if (!modalOpenRef.current) return;

      if (competitorsResponse?.ok) {
        const items = competitorsResponse.data || [];
        setData(items);
        setError(null);
        syncScrapingStateWithData(items);
      } else if (competitorsResponse?.message) {
        setError(competitorsResponse.message);
      }
    },
    [syncScrapingStateWithData]
  );

  const applyCacheSnapshot = useCallback(
    (cacheSnapshot) => {
      if (cacheInvalidationInProgressRef.current) return [];

      const items = formatCompetitorAdsCacheForGallery(cacheSnapshot);

      setData((currentItems) => {
        if (
          currentItems.length === items.length &&
          currentItems.every((item, index) => item.id === items[index]?.id)
        ) {
          syncScrapingStateWithData(currentItems);
          return currentItems;
        }

        syncScrapingStateWithData(items);
        return items;
      });

      return items;
    },
    [syncScrapingStateWithData]
  );

  const scheduleCacheSnapshotApply = useCallback(
    (cacheSnapshot) => {
      if (!cacheSnapshot || cacheInvalidationInProgressRef.current) return;

      if (cacheApplyTimerRef.current) {
        clearTimeout(cacheApplyTimerRef.current);
      }

      cacheApplyTimerRef.current = setTimeout(() => {
        applyCacheSnapshot(cacheSnapshot);
        setError(null);
        cacheApplyTimerRef.current = null;
      }, 200);
    },
    [applyCacheSnapshot]
  );

  const refreshCompetitorsAds = useCallback(
    async ({ showLoading = false, applyData = true } = {}) => {
      if (fetchInFlightRef.current) {
        pendingRefreshRef.current = { showLoading, applyData };
        return null;
      }

      fetchInFlightRef.current = true;
      if (showLoading) setIsLoading(true);

      try {
        const competitorsResponse = await dispatch(
          startGetCompetitorsAds({ platform: ALL_FILTER })
        );

        if (applyData) {
          applyCompetitorsResponse(competitorsResponse);
        } else if (showLoading && competitorsResponse?.message) {
          setError(competitorsResponse.message);
        }

        return competitorsResponse;
      } finally {
        if (showLoading) setIsLoading(false);
        fetchInFlightRef.current = false;

        const pendingRefresh = pendingRefreshRef.current;
        if (pendingRefresh) {
          pendingRefreshRef.current = null;
          void refreshCompetitorsAds(pendingRefresh);
        }
      }
    },
    [applyCompetitorsResponse, dispatch]
  );

  const refreshBrandAndCompetitorsInBackground = useCallback(async () => {
    const brandResponse = await dispatch(startGetBrandInformation());

    if (!modalOpenRef.current) return;

    if (brandResponse?.ok) {
      const normalized = normalizeApiDataToForm(brandResponse.data);
      setBusinessCompetitors(normalized.businessCompetitors || []);
    }

    await refreshCompetitorsAds({ showLoading: false, applyData: true });
  }, [dispatch, refreshCompetitorsAds]);

  const onLoadingAnimationComplete = useCallback(() => {
    if (!modalOpenRef.current) return;

    setIsLoading(false);
    setIsLoadingContentReady(false);
  }, []);

  useEffect(() => {
    if (!modalOpen) {
      clearScrapingState();
      return undefined;
    }

    setIsLoading(true);
    setIsLoadingContentReady(false);
    setLoadingDurationMs(COMPETITORS_LOADING_TIME_MS);

    dispatch(
      startListenerCompetitorAdsCache({
        onUpdatedDataCallback: scheduleCacheSnapshotApply,
        onReferenceAvailable: (dbRef) => {
          cacheListenerRef.current = dbRef;
        },
      })
    );

    return () => {
      if (cacheApplyTimerRef.current) {
        clearTimeout(cacheApplyTimerRef.current);
        cacheApplyTimerRef.current = null;
      }

      dispatch(
        stopListenerCompetitorAdsCache({
          listenerRef: cacheListenerRef.current,
        })
      );
      cacheListenerRef.current = null;
    };
  }, [clearScrapingState, dispatch, modalOpen, scheduleCacheSnapshotApply]);

  const onGetInitialData = useCallback(
    async ({ refreshAdsOnly = false } = {}) => {
      if (refreshAdsOnly) {
        return refreshCompetitorsAds({ showLoading: false, applyData: true });
      }

      if (fetchInFlightRef.current) {
        pendingRefreshRef.current = { showLoading: false, applyData: true };
        return null;
      }

      fetchInFlightRef.current = true;
      setError(null);
      setIsLoading(true);
      setIsLoadingContentReady(false);

      const loadingStartedAt = Date.now();
      let hasCachedItems = false;

      try {
        const cacheResponse = await dispatch(startGetCompetitorAdsCache());

        if (cacheResponse?.ok && cacheResponse.data) {
          const items = applyCacheSnapshot(cacheResponse.data);
          hasCachedItems = items.length > 0;

          if (hasCachedItems && modalOpenRef.current) {
            setLoadingDurationMs(COMPETITORS_CACHED_LOADING_MIN_MS);
          }
        }

        setBusinessCompetitors([]);

        if (hasCachedItems) {
          await waitForCachedLoadingMinimum({ loadingStartedAt });

          if (modalOpenRef.current) {
            setIsLoadingContentReady(true);
          }

          void refreshBrandAndCompetitorsInBackground();
          return { ok: true };
        }

        const [brandResponse, competitorsResponse] = await Promise.all([
          dispatch(startGetBrandInformation()),
          dispatch(startGetCompetitorsAds({ platform: ALL_FILTER })),
        ]);

        if (!modalOpenRef.current) return competitorsResponse;

        if (brandResponse?.ok) {
          const normalized = normalizeApiDataToForm(brandResponse.data);
          setBusinessCompetitors(normalized.businessCompetitors || []);
        }

        applyCompetitorsResponse(competitorsResponse);

        if (modalOpenRef.current) {
          setLoadingDurationMs(COMPETITORS_LOADING_TIME_MS);
          setIsLoadingContentReady(true);
        }

        return competitorsResponse;
      } finally {
        fetchInFlightRef.current = false;

        const pendingRefresh = pendingRefreshRef.current;
        if (pendingRefresh) {
          pendingRefreshRef.current = null;
          void refreshCompetitorsAds(pendingRefresh);
        }
      }
    },
    [
      applyCacheSnapshot,
      applyCompetitorsResponse,
      dispatch,
      refreshBrandAndCompetitorsInBackground,
      refreshCompetitorsAds,
    ]
  );

  const onRefreshAfterCompetitorsSaved = useCallback(
    async ({ savedCompetitors, previousCompetitors = [] }) => {
      resetGalleryState();
      cacheInvalidationInProgressRef.current = true;
      setIsLoading(true);
      setIsLoadingContentReady(false);
      setLoadingDurationMs(COMPETITORS_LOADING_TIME_MS);

      try {
        await dispatch(startInvalidateCompetitorAdsCache());

        const previousNames = new Set(
          (previousCompetitors || []).map((name) =>
            normalizeCompetitorName(name)
          )
        );

        const newlyAddedCompetitors = (savedCompetitors || [])
          .map((name) => name.trim())
          .filter(
            (name) => name && !previousNames.has(normalizeCompetitorName(name))
          );

        if (newlyAddedCompetitors.length > 0) {
          startScrapingForCompetitors({
            competitorNames: newlyAddedCompetitors,
          });
        }

        const competitorsResponse = await dispatch(
          startGetCompetitorsAds({ platform: ALL_FILTER })
        );

        if (modalOpenRef.current) {
          applyCompetitorsResponse(competitorsResponse);
          setIsLoadingContentReady(true);
        }
      } finally {
        cacheInvalidationInProgressRef.current = false;
      }
    },
    [
      applyCompetitorsResponse,
      dispatch,
      resetGalleryState,
      startScrapingForCompetitors,
    ]
  );

  const onSendSelectedItems = useCallback(
    async (
      itemsToProcess,
      {
        onCallbackSelectedItems,
        onCloseModal,
        allowVideo = false,
        acceptedReferenceMediaType = null,
      }
    ) => {
      const selectedItem = itemsToProcess[0];
      if (
        acceptedReferenceMediaType === "video" &&
        selectedItem?.type !== "video"
      ) {
        return;
      }
      if (
        acceptedReferenceMediaType !== "video" &&
        !allowVideo &&
        isMetaAdLibraryViewOnlyItem(selectedItem)
      ) {
        return;
      }

      const dataUrl = selectedItem?.url;
      if (!dataUrl || !pathStorageReferenceAd) return;

      setIsUploading(true);

      try {
        const persistResponse = await dispatch(
          startPersistMetaAdLibraryImageToStorage({
            dataUrl,
            path: pathStorageReferenceAd,
          })
        );

        if (!persistResponse?.ok || !persistResponse.url) {
          setError(
            persistResponse?.message || "The request could not be processed."
          );
          return;
        }

        const storedUrl = persistResponse.url;

        onCallbackSelectedItems?.([
          {
            url: storedUrl,
            type: selectedItem.type === "video" ? "video" : "image",
            from: selectedItem.from,
            id: selectedItem.id,
            pageId: selectedItem.pageId,
            adId: selectedItem.adId,
          },
        ]);
        onCloseModal?.();
      } finally {
        setIsUploading(false);
      }
    },
    [dispatch, pathStorageReferenceAd]
  );

  return {
    isLoading,
    isLoadingContentReady,
    loadingDurationMs,
    onLoadingAnimationComplete,
    isUploading,
    isScrapingNewCompetitors,
    data,
    error,
    businessCompetitors,
    setBusinessCompetitors,
    onGetInitialData,
    onRefreshAfterCompetitorsSaved,
    onSendSelectedItems,
  };
};

export default useCompetitorsData;
